import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { PlayCircleIcon } from '@heroicons/react/24/solid';
import {
  FeatureCheckbox,
  FeatureRadio,
  Step,
  Stepper,
} from '@praetorian-chariot/ui';
import { useEffect, useState } from 'react';

import { Modal } from '@/components/Modal';
import { useAssetActionHandlers } from '@/sections/asset/hooks/actions';
import { useGlobalState } from '@/state/global.state';
import { CrawlerRunType } from '@/types';

const steps = [
  {
    label: 'Run Types',
    value: 'runTypes',
  },
  {
    label: 'User Agent',
    value: 'userAgent',
  },
];

const runTypeOptions = [
  {
    title: 'Follow Links',
    description:
      "Chariot will do a deep crawl of all available links it can find. This is a good run type for exploring the extent of an asset's web surface.",
    type: CrawlerRunType.CRAWL,
  },
  {
    title: 'Brute Force Common Login Path',
    description:
      'This is a targeted, shallow crawl of common login paths. This is a good run type for quickly detecting if the asset has a login portal.',
    type: CrawlerRunType.LOGIN,
  },
  {
    title: 'Brute Force Common Directories/File Paths',
    description:
      'This is a wide, shallow brute-force check of common attack entry points. This is a good run type for discovering hidden well-known pages that could aid attackers in exploiting the asset.',
    type: CrawlerRunType.DIRBUSTER,
  },
];

export const SelectUserAgentModal = () => {
  const {
    modal: { selectUserAgent },
  } = useGlobalState();
  const handlers = useAssetActionHandlers();
  const open = selectUserAgent.assetKeys.length > 0;

  const [runTypes, setRunTypes] = useState<CrawlerRunType[]>();
  const [randomUserAgent, setRandomUserAgent] = useState<boolean>();
  const [step, setStep] = useState<Step>();

  useEffect(() => {
    if (open) {
      setRunTypes([CrawlerRunType.CRAWL]);
      setRandomUserAgent(false);
      setStep(steps[0]);
    }
  }, [open]);

  useEffect(() => {
    return () => {
      handleClose();
    };
  }, []);

  function toggleRunType(type: CrawlerRunType) {
    setRunTypes(
      runTypes?.includes(type)
        ? runTypes.filter(t => t !== type)
        : [...(runTypes || []), type]
    );
  }

  function handleClose() {
    selectUserAgent.onAssetKeysChange([]);
  }

  function handleStartScan() {
    handlers.handleScan(
      selectUserAgent.assetKeys.map(key => ({ key })),
      'crawl',
      { randomUserAgent: randomUserAgent ?? false, runTypes: runTypes ?? [] }
    );
    handleClose();
  }

  return (
    <Modal
      title="Web Crawl"
      footer={
        step?.value === 'runTypes'
          ? {
              className: 'w-fit',
              text: 'Next: User Agent',
              onClick: () => {
                setStep(steps[1]);
              },
              disabled: runTypes?.length === 0,
              endIcon: <ChevronRightIcon className="size-4" />,
            }
          : {
              className: 'w-fit',
              text: 'Start Scan',
              onClick: handleStartScan,
              startIcon: <PlayCircleIcon className="size-4" />,
            }
      }
      open={open}
      onClose={handleClose}
    >
      <Stepper
        className="mb-4"
        steps={steps}
        selected={step ?? null}
        onSelect={setStep}
      />

      {step?.value === 'runTypes' && (
        <div className="flex flex-col gap-4">
          <h3 className="text-lg font-bold">Run Type Options</h3>
          {runTypeOptions.map(({ title, description, type }) => (
            <FeatureCheckbox
              className="w-full"
              key={type}
              id={type}
              checked={runTypes?.includes(type)}
              title={title}
              description={description}
              onChange={() => {
                toggleRunType(type);
              }}
            />
          ))}
        </div>
      )}
      {step?.value === 'userAgent' && (
        <div className="flex flex-col gap-4">
          <h3 className="text-lg font-bold">Select User Agent</h3>
          <FeatureRadio
            id="dynamic"
            checked={!randomUserAgent}
            title="Chariot User Agent"
            description={`Chariot scans use a unique and consistent user agent to allow easy tracing of Chariot's requests. However, Chariot's requests may be blocked by default network precautions.`}
            onChange={() => setRandomUserAgent(false)}
          />
          <FeatureRadio
            id="static"
            checked={randomUserAgent}
            title="Random User Agent"
            description={`Stealth scans use a randomized user agent that aids in bypassing anti-bot protections. You will not be able to trace a given request back to Chariot.`}
            onChange={() => setRandomUserAgent(true)}
          />
        </div>
      )}
    </Modal>
  );
};
